import * as Sentry from '@sentry/nextjs'
import { BREAKPOINTS, DSSpacer, DSText } from '@zoe/ds-web'
import styled from 'styled-components'

import { useBanner } from '@/context/bannerContext'

import { processTypographyMarkdown } from '@/components/blocks/DesignSystem/Typography'

import { resolveCmsLinkTarget } from '@/lib/attribution/firstPartyUrl'
import { useTrackedRouter } from '@/lib/attribution/useTrackedRouter'
import { trackCtaClick } from '@/lib/gtm/gtmV2'

export const BANNER_HEIGHT_DESKTOP = 44
export const BANNER_HEIGHT_MOBILE = 74

const StyledBanner = styled.div`
  position: fixed;
  background-color: var(--color-surface-navy-bold);
  top: 0;
  right: 0;
  width: 100%;
  max-width: 100%;
  z-index: 4;
  min-height: ${BANNER_HEIGHT_DESKTOP}px;
  margin-bottom: var(--grid-12);
  @media screen and (max-width: ${BREAKPOINTS.m}) {
    height: ${BANNER_HEIGHT_MOBILE}px;
  }
  span {
    color: var(--colour-icon-brand);
    font-weight: var(--font-sans-serif-semibold-weight);
  }
`

const StyledBannerTextContainer = styled.div<{ $hasUrl: boolean }>`
  max-width: 90%;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;

  ${({ $hasUrl }) =>
    $hasUrl &&
    `
    cursor: pointer;
    &:hover {
      transform: scale(1.05);
      transition: transform 0.3s ease;
    }
  `}
`

export const Banner = () => {
  const { bannerContent } = useBanner()
  const router = useTrackedRouter()

  if (!bannerContent?.enabled || !bannerContent?.text) return null

  const handleClick = () => {
    if (bannerContent?.url) {
      const target = resolveCmsLinkTarget(bannerContent.url)
      if (target?.kind === 'internal') {
        void router.push(target.href)
      } else if (target?.kind === 'external') {
        window.location.href = target.href
      } else {
        Sentry.captureMessage('Banner url is not a navigable link', {
          level: 'warning',
          extra: { url: bannerContent.url },
        })
      }
    }
    trackCtaClick('banner', bannerContent?.url || '', { link_position: 'header' })
  }

  return (
    <StyledBanner>
      <DSSpacer size={12} direction="vertical" breakAt={{ m: 0 }} />
      <StyledBannerTextContainer onClick={handleClick} $hasUrl={!!bannerContent?.url}>
        <DSText variant="fluid-paragraph-200" color="primary-inverse" align="center">
          {processTypographyMarkdown({
            content: bannerContent?.text,
            variant: `fluid-paragraph-200`,
            weight: 'regular',
          })}
        </DSText>
      </StyledBannerTextContainer>
      <DSSpacer size={12} direction="vertical" breakAt={{ m: 0 }} />
    </StyledBanner>
  )
}
